/**
 * 1.6.5 — one press is not an accident (D-1.6.5-1): anything that changes a visit's state from the crew phone is
 * confirmed in one line, can be undone for ten minutes, and shows who did it and when on the card — for everyone
 * looking at that card, not only the one who pressed.
 *
 *   Arrived          "Arrived 9:38 · Brigida" + Undo (ARRIVAL_UNDO_MINUTES); after the window the office clears it.
 *   Mark completed   asks first; "Completed 9:01 · Brigida" + Undo for the same minutes.
 *   One open visit   a crew cannot start a unit while an earlier visit of its own is still open (D-1.6.5-3, per
 *                    crew: A / B from Admin → Users, D-1.6.5-3b). The office is exempt.
 *   Two phones       the first press wins; the second is told who pressed (D-1.6.5-4).
 *   Open too long    planned length + HK_OPEN_TOO_LONG_MINUTES: "Open 2 h 10 min — still there?" (D-1.6.5-5).
 *
 * No list and no column: the stamps live in the arrival store and `_app/marks/`, the presses in the app's audit.
 */
import type { CrewAssignment } from './admin';
import { staffName } from './people';
import { startsVisit, type ArrivalMarker } from './arrival';

export type CrewTag = CrewAssignment;
/** On a normal day everyone is crew A; the second team is set to B once in Admin → Users. */
export const DEFAULT_CREW: CrewTag = 'A';
export const crewOf = (crew: CrewTag | null | undefined): CrewTag => (crew === 'B' ? 'B' : DEFAULT_CREW);

export const UNDO_REASONS = ['mistake', 'other'] as const;
export type UndoReason = (typeof UNDO_REASONS)[number];
/** The words kept in the audit for each reason (English: the office's record). */
export const UNDO_REASON_WORDS: Record<UndoReason, string> = { mistake: 'pressed by mistake', other: 'other' };

/** An undone stamp: who took it back, when and why. `office`: cleared from Office action, after the window. */
export interface StampUndo {
  at: string;
  by: string;
  reason: UndoReason;
  words: string;
  office: boolean;
}

export interface UndoRequest {
  appointmentId: string;
  reason: UndoReason;
  words?: string;
}

/** Mark completed (a staff unit or an office): who pressed, and what the record held before — Undo puts it back. */
export interface MarkRecord {
  appointmentId: string;
  unit: string;
  dayKey: string;
  at: string;
  by: string;
  role: string;
  crew: CrewTag;
  /** The columns the press changed, as they were. */
  previous: Record<string, unknown>;
  undone?: StampUndo | null;
}

/** What Today shows for a marked visit. */
export interface MarkMarker {
  at: string;
  by: string;
  crew: CrewTag;
}

export const markMarker = (r: MarkRecord): MarkMarker => ({ at: r.at, by: r.by, crew: r.crew });

/** The second press on a stamped card: who was first (the 409's body, beside `error` and `message`). */
export interface AlreadyPressed {
  by: string;
  at: string;
}

/** "Close out 703 first." — the earlier visit of the same crew that is still open. */
export interface CloseOutFirst {
  appointmentId: string;
  unit: string;
}

export const ARRIVAL_UNDO_KEY = 'ARRIVAL_UNDO_MINUTES';
export const OPEN_TOO_LONG_KEY = 'HK_OPEN_TOO_LONG_MINUTES';
export const DEFAULT_UNDO_MINUTES = 10;
export const DEFAULT_OPEN_TOO_LONG_MINUTES = 30;
export const UNDO_MINUTE_STEPS: readonly number[] = [5, 10, 15, 20, 30];
export const OPEN_TOO_LONG_STEPS: readonly number[] = [15, 30, 45, 60, 90];

function minutesOf(settings: Record<string, string | undefined> | undefined, key: string, fallback: number, max: number): number {
  const n = Number(settings?.[key]);
  return Number.isFinite(n) && n >= 1 && n <= max ? n : fallback;
}
export const undoMinutes = (settings?: Record<string, string | undefined>): number => minutesOf(settings, ARRIVAL_UNDO_KEY, DEFAULT_UNDO_MINUTES, 120);
export const openTooLongMinutes = (settings?: Record<string, string | undefined>): number => minutesOf(settings, OPEN_TOO_LONG_KEY, DEFAULT_OPEN_TOO_LONG_MINUTES, 8 * 60);

/** Is Undo still offered for a press made at `at`? */
export function undoOpen(at: string, minutes: number, now: Date = new Date()): boolean {
  const t = Date.parse(at);
  return Number.isFinite(t) && now.getTime() - t < minutes * 60_000 && now.getTime() >= t - 60_000;
}

/** "Brigida" from the sign-in address — the name on the card. */
export function pressName(email: string): string {
  return staffName(email).split(' ')[0] || email;
}

/** Whole minutes a visit has been open since its arrival. */
export function minutesOpen(at: string, now: Date = new Date()): number {
  const t = Date.parse(at);
  return Number.isFinite(t) ? Math.max(0, Math.floor((now.getTime() - t) / 60_000)) : 0;
}

/** Arrived (tenant home, or entered with permission) and open longer than its planned length plus the setting. */
export function openTooLong(arrival: Pick<ArrivalMarker, 'answer' | 'at'> | null | undefined, lengthMinutes: number, extraMinutes: number, now: Date = new Date()): number | null {
  if (!arrival || !startsVisit(arrival.answer)) return null;
  const open = minutesOpen(arrival.at, now);
  return open > lengthMinutes + extraMinutes ? open : null;
}

/** "2 h 10 min", "45 min" — the same in English and Spanish. */
export function openWords(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? (m ? `${h} h ${m} min` : `${h} h`) : `${m} min`;
}

interface OpenRuleVisit {
  appointmentId: string;
  unit: string;
  position: number | null;
  status: string;
  /** A close-out has been pressed for the visit (recorded or with the scheduler). */
  closedOut: boolean;
  arrival: Pick<ArrivalMarker, 'answer' | 'crew'> | null | undefined;
}

/**
 * The one-open-visit rule: the earlier visit of the day (a lower position) that this crew started and has not
 * closed out. "No answer", "cancelled at the door" and a Finish all close a visit for this rule.
 */
export function closeOutFirst(visits: readonly OpenRuleVisit[], target: Pick<OpenRuleVisit, 'appointmentId' | 'position'>, crew: CrewTag): CloseOutFirst | null {
  if (target.position === null) return null;
  const open = visits
    .filter((v) => v.appointmentId !== target.appointmentId && v.position !== null && v.position < target.position!)
    .filter((v) => (v.status === 'Scheduled' || v.status === 'In Progress') && !v.closedOut)
    .filter((v) => v.arrival && startsVisit(v.arrival.answer) && crewOf(v.arrival.crew) === crew)
    .sort((a, b) => a.position! - b.position!)[0];
  return open ? { appointmentId: open.appointmentId, unit: open.unit } : null;
}

/**
 * 1.6.6: the same rule on a two-crew day, run within the lane — an earlier visit of the lane (by its place in the
 * lane, not its position on the day) that was started and is not closed out. Whoever stamped it, it is the lane's.
 */
export function closeOutFirstInLane(visits: readonly OpenRuleVisit[], target: Pick<OpenRuleVisit, 'appointmentId' | 'position'>, lane: CrewTag, spots: Readonly<Record<number, { crew: CrewTag; n: number }>>): CloseOutFirst | null {
  const place = (p: number | null) => (p !== null && spots[p]?.crew === lane ? spots[p]!.n : null);
  const inLane = visits.filter((v) => place(v.position) !== null).map((v) => ({ ...v, position: place(v.position), arrival: v.arrival ? { ...v.arrival, crew: lane } : v.arrival }));
  return closeOutFirst(inLane, { appointmentId: target.appointmentId, position: place(target.position) }, lane);
}

/** Tags show only when the day has more than one crew at work: in the stamps, or among the crew accounts. */
export function crewTagsOn(stampCrews: readonly (CrewTag | null | undefined)[], userCrews: readonly (CrewTag | null | undefined)[]): boolean {
  const seen = new Set([...stampCrews, ...userCrews].map((c) => crewOf(c)));
  return seen.size > 1;
}
